<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <div class="flex items-center justify-between">
          <div>
            <button
              @click="$router.back()"
              class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Orders
            </button>
            <h1 class="text-3xl font-bold text-gray-900">Order Details</h1>
            <p class="mt-2 text-gray-600">Order #{{ order?.order_no }}</p>
          </div>
          <div class="text-right">
            <div class="text-sm text-gray-500">{{ order?.product_count || 0 }} {{ order?.product_count === 1 ? 'product' : 'products' }}, {{ order?.item_count || 0 }} {{ order?.item_count === 1 ? 'item' : 'items' }}</div>
            <div class="text-sm text-gray-500">We'll contact you with pricing details</div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div class="spinner w-8 h-8 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading order details...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div class="text-red-600 mb-4">
          <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Failed to load order</h3>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <button
          @click="loadOrder"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          Try Again
        </button>
      </div>

      <!-- Order Details -->
      <div v-else-if="order" class="space-y-6">
        <!-- Order Status -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">Order Status</h2>
          <div class="flex items-center space-x-4">
            <div class="flex-1">
              <div class="flex items-center space-x-2">
                <div :class="getStatusColor(order.status)" class="w-4 h-4 rounded-full"></div>
                <span class="text-lg font-medium text-gray-900">{{ getStatusText(order.status, order) }}</span>
              </div>
                              <p class="text-sm text-gray-500 mt-1">{{ getStatusDescription(order.status, order) }}</p>
            </div>
            <div v-if="order.due_date" class="text-right">
              <p class="text-sm text-gray-500">Due Date</p>
              <p class="text-lg font-medium text-gray-900">{{ formatDate(order.due_date) }}</p>
            </div>
          </div>
        </div>

        <!-- Customer Information -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">Customer Information</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Phone</p>
              <p class="text-sm font-medium text-gray-900">{{ order.customer_phone || 'Not provided' }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500">Email</p>
              <p class="text-sm font-medium text-gray-900">{{ order.customer_email || 'Not provided' }}</p>
            </div>
            <div class="md:col-span-2">
              <p class="text-sm text-gray-500">Delivery Address</p>
              <p class="text-sm font-medium text-gray-900">{{ order.customer_address || 'Not provided' }}</p>
            </div>
            <div v-if="order.gst_number" class="md:col-span-2">
              <p class="text-sm text-gray-500">GST Number</p>
              <p class="text-sm font-medium text-gray-900">{{ order.gst_number }}</p>
            </div>
          </div>
        </div>

        <!-- Order Items -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">Order Items</h2>
          
          <!-- Combined Totals Section -->
          <div v-if="order.items && order.items.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 class="font-semibold text-blue-900 mb-3">Order Summary</h4>
            <div class="grid grid-cols-3 gap-4 text-center">
              <div>
                <p class="text-sm text-blue-600">Total Quantity</p>
                <p class="text-lg font-bold text-blue-900">{{ getTotalQuantity(order.items) }}</p>
              </div>
              <div>
                <p class="text-sm text-blue-600">Total Gross Weight</p>
                <p class="text-lg font-bold text-blue-900">{{ getTotalGrossWeight(order.items) }}g</p>
              </div>
              <div>
                <p class="text-sm text-blue-600">Total Net Weight</p>
                <p class="text-lg font-bold text-blue-900">{{ getTotalNetWeight(order.items) }}g</p>
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <div
              v-for="item in order.items"
              :key="item.id"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex gap-4">
                <!-- Product Images -->
                <div class="flex-shrink-0">
                  <div v-if="item.images && item.images.length > 0" class="relative group cursor-pointer" @click="viewImageModal(item.images, 0, item.name)" title="Click to view full size">
                    <img
                      :src="getImageUrl(item.images[0])"
                      :alt="item.name"
                      class="w-20 h-20 object-contain rounded-lg border border-gray-200 hover:opacity-80 hover:border-orange-500 hover:shadow-md transition-all duration-200"
                      @error="handleImageError"
                    />
                    <div v-if="item.images.length > 1" class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      +{{ item.images.length - 1 }}
                    </div>
                    <!-- Hover overlay -->
                    <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg flex items-center justify-center transition-all duration-200">
                      <svg class="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <div v-else class="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center border border-gray-200">
                    <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                
                <!-- Product Details -->
                <div class="flex-1">
                  <div class="flex justify-between items-start">
                    <div>
                      <h3 class="text-lg font-medium text-gray-900">{{ item.name }}</h3>
                      <p class="text-sm text-gray-600">SKU: {{ item.sku }}</p>
                      <p class="text-sm text-gray-600">Category: {{ item.category }}</p>
                      <p class="text-sm text-gray-600">Metal: {{ item.metal }}</p>
                      <div class="mt-2 space-y-1">
                        <p class="text-sm text-gray-600">
                          <span class="font-medium">Gross Weight:</span> {{ item.gross_weight || 0 }}g
                        </p>
                        <p class="text-sm text-gray-600">
                          <span class="font-medium">Net Weight:</span> {{ item.net_weight || 0 }}g
                        </p>
                      </div>
                    </div>
                    <div class="text-right">
                      <p class="text-sm font-medium text-gray-900">Qty: {{ item.quantity || item.qty }}</p>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">Gross Total:</span> {{ getItemGrossTotal(item) }}g
                      </p>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">Net Total:</span> {{ getItemNetTotal(item) }}g
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Timeline -->
        <div v-if="order.history && order.history.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">Order Timeline</h2>
          <div class="space-y-4">
            <div
              v-for="(event, index) in order.history"
              :key="event.id"
              class="flex items-start space-x-4"
            >
              <div class="flex-shrink-0">
                <div class="w-3 h-3 bg-primary-500 rounded-full mt-2"></div>
                <div v-if="index < order.history.length - 1" class="w-0.5 h-8 bg-gray-200 mx-auto mt-1"></div>
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">{{ getStatusText(event.new_status, order) }}</p>
                <p class="text-xs text-gray-500">{{ formatDate(event.created_at) }}</p>
                <p v-if="event.notes" class="text-sm text-gray-600 mt-1">{{ event.notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Actions -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-medium text-gray-900 mb-4">Actions</h2>
          <div class="flex space-x-4">
            <button
              v-if="order.status === 'pending' && !order.assigned_workshop_user"
              @click="showCancelOrderModal"
              class="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              Cancel Order
            </button>
            <button
              @click="$router.push('/my-orders')"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    </div>
    <NotificationManager />
    
    <!-- Cancel Order Confirmation Modal -->
    <ConfirmDialog
      :visible="showCancelConfirm"
      title="Cancel Order"
      message="Are you sure you want to cancel this order? This action cannot be undone."
      confirm-text="Yes, Cancel Order"
      cancel-text="Keep Order"
      @confirm="cancelOrder"
      @cancel="cancelCancelOrder"
    />

    <!-- Image Modal -->
    <div v-if="showImageModal" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]" @click="closeImageModal">
      <div class="relative max-w-4xl max-h-[90vh] mx-4" @click.stop>
        <div class="bg-white rounded-lg overflow-hidden">
          <div class="flex justify-between items-center p-4 border-b">
            <h3 class="text-lg font-semibold text-gray-900">{{ currentProductName || (currentImages.length > 0 ? 'Product Images' : 'No Images Available') }}</h3>
            <button @click="closeImageModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="p-4">
            <div v-if="currentImages.length > 0" class="space-y-4">
              <div class="relative">
                <div class="w-full max-w-3xl mx-auto bg-gradient-to-br from-orange-100 to-red-200 rounded-xl overflow-hidden flex items-center justify-center" style="min-height:360px; max-height:480px;">
                  <img 
                    :src="getImageUrl(currentImages[currentImageIndex])" 
                    :alt="`Product Image ${currentImageIndex + 1}`"
                    class="w-full h-full object-contain object-center"
                    style="min-height:360px; max-height:480px;"
                    @error="handleImageError"
                  />
                </div>
                <button 
                  v-if="currentImages.length > 1"
                  @click="previousImage"
                  class="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
                >
                  <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  v-if="currentImages.length > 1"
                  @click="nextImage"
                  class="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
                >
                  <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1 text-white text-sm font-medium">
                  {{ currentImageIndex + 1 }} / {{ currentImages.length }}
                </div>
              </div>
              <div v-if="currentImages.length > 1" class="flex space-x-2 overflow-x-auto pb-2">
                <button
                  v-for="(image, index) in currentImages"
                  :key="index"
                  @click="currentImageIndex = index"
                  :class="[
                    'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200',
                    currentImageIndex === index 
                      ? 'border-orange-500 shadow-lg' 
                      : 'border-gray-200 hover:border-orange-300'
                  ]"
                >
                  <img 
                    :src="getImageUrl(image)" 
                    :alt="`Thumbnail ${index + 1}`"
                    class="w-full h-full object-contain"
                    @error="handleImageError"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ordersAPI } from '../services/api'
import { useNotificationStore } from '@/stores/notification'
import NotificationManager from '@/components/NotificationManager.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

export default {
  name: 'OrderDetails',
  components: {
    NotificationManager,
    ConfirmDialog
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const notificationStore = useNotificationStore()
    
    // State
    const loading = ref(false)
    const error = ref('')
    const order = ref(null)
    const showCancelConfirm = ref(false)
    
    // Image modal state
    const showImageModal = ref(false)
    const currentImages = ref([])
    const currentImageIndex = ref(0)
    const currentProductName = ref('')

    // Methods
    const loadOrder = async () => {
      loading.value = true
      error.value = ''
      
      try {
        const response = await ordersAPI.getOrderDetails(route.params.id)
        order.value = response.data.order
      } catch (err) {
        console.error('Failed to load order:', err)
        error.value = err.response?.data?.message || 'Failed to load order details'
        notificationStore.showError('Error', error.value)
      } finally {
        loading.value = false
      }
    }

    const showCancelOrderModal = () => {
      showCancelConfirm.value = true
    }

    const cancelOrder = async () => {
      try {
        await ordersAPI.cancelOrder(order.value.id)
        showCancelConfirm.value = false
        await loadOrder() // Reload order
        notificationStore.showSuccess('Order Cancelled', 'Your order has been cancelled successfully.')
      } catch (err) {
        console.error('Failed to cancel order:', err)
        const errorMessage = err.response?.data?.message || 'Failed to cancel order. Please try again.'
        notificationStore.showError('Cancellation Failed', errorMessage)
      }
    }

    const cancelCancelOrder = () => {
      showCancelConfirm.value = false
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }



    const getStatusColor = (status) => {
      const colors = {
        'pending': 'bg-yellow-400',
        'in_workshop': 'bg-blue-400',
        'qc': 'bg-purple-400',
        'completed': 'bg-green-400',
        'delivered': 'bg-green-600',
        'cancelled': 'bg-red-400'
      }
      return colors[status] || 'bg-gray-400'
    }

    const getStatusText = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, show "Pending in Workshop"
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'Pending in Workshop'
      }
      
      const texts = {
        'pending': 'Pending',
        'in_workshop': 'In Workshop',
        'qc': 'Quality Check',
        'completed': 'Completed',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled'
      }
      return texts[status] || status
    }

    const getStatusDescription = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, show workshop description
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'Your order has been assigned to our workshop'
      }
      
      const descriptions = {
        'pending': 'Your order is being processed',
        'in_workshop': 'Your jewelry is being crafted',
        'qc': 'Quality check in progress',
        'completed': 'Your order is ready for delivery',
        'delivered': 'Order has been delivered',
        'cancelled': 'Order has been cancelled'
      }
      return descriptions[status] || ''
    }

    const getImageUrl = (imageName) => {
      // Use a working placeholder service or return a fallback
      if (!imageName) {
        return 'data:image/svg+xml;base64,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'
      }
      
      // If it's a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    const handleImageError = (event) => {
      // Set a fallback image when the actual image fails to load
      event.target.src = 'data:image/svg+xml;base64,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'
    }

    // Helper methods for weight calculations
    const getTotalQuantity = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => total + (item.quantity || item.qty || 0), 0)
    }

    const getTotalGrossWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const grossWeight = parseFloat(item.gross_weight) || 0
        return total + (grossWeight * quantity)
      }, 0).toFixed(2)
    }

    const getTotalNetWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const netWeight = parseFloat(item.net_weight) || 0
        return total + (netWeight * quantity)
      }, 0).toFixed(2)
    }

    const getItemGrossTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const grossWeight = parseFloat(item.gross_weight) || 0
      return (grossWeight * quantity).toFixed(2)
    }

    const getItemNetTotal = (item) => {
      const quantity = item.quantity || item.qty || 0
      const netWeight = parseFloat(item.net_weight) || 0
      return (netWeight * quantity).toFixed(2)
    }

    // Image modal methods
    const viewImageModal = (images, startIndex = 0, productName = '') => {
      currentImages.value = images
      currentImageIndex.value = startIndex
      currentProductName.value = productName
      showImageModal.value = true
    }

    const closeImageModal = () => {
      showImageModal.value = false
      currentImages.value = []
      currentImageIndex.value = 0
      currentProductName.value = ''
    }

    const nextImage = () => {
      if (currentImages.value.length > 1) {
        currentImageIndex.value = (currentImageIndex.value + 1) % currentImages.value.length
      }
    }

    const previousImage = () => {
      if (currentImages.value.length > 1) {
        currentImageIndex.value = currentImageIndex.value === 0 
          ? currentImages.value.length - 1 
          : currentImageIndex.value - 1
      }
    }

    // Lifecycle
    onMounted(() => {
      loadOrder()
    })

    return {
      loading,
      error,
      order,
      showCancelConfirm,
      loadOrder,
      showCancelOrderModal,
      cancelOrder,
      cancelCancelOrder,
      formatDate,
      getStatusColor,
      getStatusText,
      getStatusDescription,
      getImageUrl,
      handleImageError,

      // Weight calculation methods
      getTotalQuantity,
      getTotalGrossWeight,
      getTotalNetWeight,
      getItemGrossTotal,
      getItemNetTotal,
      
      // Image modal methods
      showImageModal,
      currentImages,
      currentImageIndex,
      currentProductName,
      viewImageModal,
      closeImageModal,
      nextImage,
      previousImage
    }
  }
}
</script> 